import {Group, Mesh, MeshBasicMaterial, PlaneGeometry, Vector3, Vector4} from 'three'

import type {BrandedDividerParams} from '../../components/WebGLContext/WebGLContext.types'
import {getRenderer} from '../_commonObjects'
import {windowSize} from '../_config'
import {PARALLAX_ROTATION} from '../modules/cube/_config'
import type {CubeConfig, CubeLayout} from '../modules/cube/_types'
import type RoundedCubeGeometry from '../modules/cube/RoundedCubeGeometry'
import SingleCube from '../modules/cube/SingleCube'
import cubeLayout0 from './cubeLayouts/cubeLayout0'
import cubeLayout1 from './cubeLayouts/cubeLayout1'
import cubeLayout2 from './cubeLayouts/cubeLayout2'
import cubeLayout2_2 from './cubeLayouts/cubeLayout2_2'
import cubeLayout3 from './cubeLayouts/cubeLayout3'
import cubeLayout4 from './cubeLayouts/cubeLayout4'
import cubeLayout5 from './cubeLayouts/cubeLayout5'
import cubeLayout6 from './cubeLayouts/cubeLayout6'
import cubeLayout7 from './cubeLayouts/cubeLayout7'
import cubeLayout8 from './cubeLayouts/cubeLayout8'
import cubeLayout9 from './cubeLayouts/cubeLayout9'
import GlassEffect from './GlassEffect'

const cubeLayout: CubeLayout = {
  mona: [cubeLayout0, cubeLayout1, cubeLayout2],
  copilot: [cubeLayout3, cubeLayout4, cubeLayout2_2],
  security: [cubeLayout5, cubeLayout0, cubeLayout6, cubeLayout7],
  invertocat: [cubeLayout0, cubeLayout8, cubeLayout9],
}

export default class Cubes {
  group: Group = new Group()
  group2: Group = new Group()
  singleCubes: SingleCube[] = []
  cubeGeometry: RoundedCubeGeometry
  glassEffect: GlassEffect
  rectData: Vector4
  _rectData: Vector4
  parentGroup: Group
  singleBigCube: SingleCube | null = null // for copilot layout 0
  tl: gsap.core.Timeline

  constructor(cubeGeometry: RoundedCubeGeometry, parentGroup: Group, tl: gsap.core.Timeline) {
    this.parentGroup = parentGroup
    this.rectData = parentGroup.userData.rectData as Vector4
    this._rectData = parentGroup.userData._rectData as Vector4
    this.cubeGeometry = cubeGeometry
    this.glassEffect = new GlassEffect(this._rectData)
    this.tl = tl

    this.group.add(this.group2)
  }

  createCubes(params: BrandedDividerParams) {
    let cl
    cl = cubeLayout[params.character][params.layout]

    if (!cl) return

    this.group2.rotation.order = cl.rotationOrder
    this.group2.rotation.x = cl.rotation.x
    this.group2.rotation.y = cl.rotation.y
    this.group2.rotation.z = cl.rotation.z

    this.group2.userData.defRotation = this.group2.rotation.clone()

    this.group2.position.copy(cl.groupAnim.start)

    this.tl.to(
      this.group2.position,
      {
        x: cl.groupAnim.end.x,
        y: cl.groupAnim.end.y,
        z: cl.groupAnim.end.z,
        ease: cl.groupAnim.ease,
        duration: cl.groupAnim.duration,
      },
      0,
    )

    // const helper = new AxesHelper(5);
    // this.group2.add(helper);

    if (params.character === 'copilot' && params.layout === 0) {
      const cubeConfig = {
        position: new Vector3(2, -1.2, 0.0),
        rotation: new Vector3((Math.PI * 4) / 5, Math.PI * 0.25, 0),
        cubeType: 'normal',
        isGlassRender: false,
        offsetStart: {
          position: new Vector3(0, -4, 0),
          rotation: new Vector3(-Math.PI, 0, 0),
        },
        delay: 0.0,
        duration: 2,
        scale: 1.4,
        layout: {
          tablet: {
            offset: new Vector3(0, 0, 0),
            visible: true,
          },
          mobile: {
            offset: new Vector3(0, 0, 0),
            visible: false,
          },
        },
      }

      this.singleBigCube = new SingleCube({
        cubeConfig,
        cubeGeometry: this.cubeGeometry,
        glassEffect: this.glassEffect,
        rectData: this.rectData,
        _rectData: this._rectData,
      })

      this.singleBigCube.createAnimation(this.tl)
      this.singleCubes.push(this.singleBigCube)
      this.group.add(this.singleBigCube.mesh)
    }

    cl.cubes.forEach((cubeConfig: CubeConfig) => {
      const singleCube = new SingleCube({
        cubeConfig,
        cubeGeometry: this.cubeGeometry,
        glassEffect: this.glassEffect,
        rectData: this.rectData,
        _rectData: this._rectData,
      })
      singleCube.createAnimation(this.tl)
      const cubeMesh = singleCube.mesh
      this.singleCubes.push(singleCube)
      this.group2.add(cubeMesh)

      if (cubeConfig.isGlassRender) {
        this.glassEffect.createSimpleCube(cubeMesh)
        // create debug of fbo of glass effect
        // this.createDebugGlass()
      }
    })
  }

  createDebugGlass() {
    const debugPlane = new Mesh(
      new PlaneGeometry(1, 1),
      new MeshBasicMaterial({map: this.glassEffect.fbo1.texture, depthTest: false}),
    )

    debugPlane.renderOrder = 9999

    // resize debug plane to match fbo size

    setTimeout(() => {
      const width = this.glassEffect.fboSize.x * 0.3
      const height = this.glassEffect.fboSize.y * 0.3
      debugPlane.scale.set(width, height, 1)
      // place top left corner of screen
      debugPlane.position.set(-windowSize.x / 2 + width / 2, windowSize.y / 2 - height / 2, 0)
    }, 200)

    let scene
    // find root scene from parents' parent... and add debug plan
    // not just this.parentGroup.parent
    let obj: any = this.parentGroup
    while (obj.parent) {
      obj = obj.parent
      if (obj.type === 'Scene') {
        scene = obj
        break
      }
    }

    scene.add(debugPlane)
  }

  resize() {
    this.glassEffect.resize()
  }

  update(scale: number, scrollProgress: number, groupPosition: Vector3) {
    const renderer = getRenderer()
    if (!renderer) return

    this.singleCubes.forEach(singleCube => {
      singleCube.update(renderer)
    })

    this.group2.rotation.x = this.group2.userData.defRotation.x - scrollProgress * Math.PI * PARALLAX_ROTATION

    if (this.singleBigCube) {
      this.singleBigCube.mesh.rotation.x -= scrollProgress * Math.PI * PARALLAX_ROTATION
    }

    this.glassEffect.update(this.group2, scale, groupPosition)
  }
}
